import { Capacitor } from "@capacitor/core"

/** True inside the iOS/Android shell (Capacitor injects the bridge into the
 *  page the shell loads from boothapp.co). False in every browser. */
export function isNativeApp(): boolean {
  try {
    return Capacitor.isNativePlatform()
  } catch {
    return false
  }
}

/** Custom URL scheme the shell registers (Info.plist CFBundleURLTypes) — the
 *  way a system browser hands a result back to the app. */
export const NATIVE_SCHEME = "co.boothapp.app"
export const NATIVE_AUTH_CALLBACK = `${NATIVE_SCHEME}://auth/callback`

/**
 * Marketing surfaces: pages that sell BOOTH rather than run it.
 *
 * The native shell opens on sign-in and never shows the landing page, so a
 * nav item pointing at the journal or the pricing table drops someone into
 * the website with no way back into the product. These come out of every nav
 * in the app.
 *
 * Deliberately NOT listed: /privacy and /terms. Apple requires the privacy
 * policy to be reachable inside the app, not only from the store listing.
 * They are reached from account settings instead of the nav.
 *
 * Product surfaces — /listings, /search, /hiring, /barbers — are not here
 * either. Browsing shops without an account is the product, and it is also
 * what keeps the app clear of the forced-registration rule.
 */
export const WEB_ONLY_PATHS = [
  "/pricing",
  "/blog",
  "/press",
  "/traction",
  "/changelog",
  "/rewards",
  "/compare",
] as const

/** True when `href` points at a marketing surface. Ignores the query string
 *  so "/pricing?audience=client" matches "/pricing". */
export function isWebOnlyPath(href: string | undefined): boolean {
  if (!href) return false
  const path = href.split("?")[0].replace(/\/$/, "")
  return WEB_ONLY_PATHS.some((p) => path === p || path.startsWith(`${p}/`))
}

/**
 * The token capacitor.config.ts appends to the web view's user agent. Keep
 * the two in step — nothing enforces it but the server's ability to tell an
 * app request apart depends on it.
 */
export const NATIVE_UA_TOKEN = "BOOTHApp"

/**
 * True when a request came from the native shell, decided on the server.
 *
 * isNativeApp() cannot help here: it runs in the browser, and by the time it
 * answers the HTML has already been sent. Anything that must not reach the
 * app at all — the advertising pixels — has to be decided from the request.
 */
export function isNativeUserAgent(userAgent: string | null | undefined): boolean {
  return !!userAgent && userAgent.includes(NATIVE_UA_TOKEN)
}
